import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import CurrencyInput from "~/components/formElements/CurrencyInput";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import PaymentTargetDaysInput from "~/components/formElements/PaymentTargetInput";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useClientsContactPersonsSelectOptions
	from "~/hooks/form/formOptionsData/useClientsContactPersonsSelectOptions.tsx";
import useClientsInvoiceRecipientSelectOptions
	from "~/hooks/form/formOptionsData/useClientsInvoiceRecipientsSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateQuotaInvoicingDetails } from "~/modules/client/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations, UpdateQuotaInvoicingDetailsData } from "~/modules/client/api/quota/quotaTypes.ts";
import InvoiceRecipientFormSection from "~/modules/project/components/forms/formSections/InvoiceRecipientFormSection";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { getPaymentTargetDefaultValues, preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData =
	Omit<UpdateQuotaInvoicingDetailsData, "invoiceDeliveryEndDate" | "invoiceDeliveryStartDate">
	& {
	invoiceDeliveryStartDate: Date;
	invoiceDeliveryEndDate: Date;
	paymentTargetDaysCustom?: number | null;
}
type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations
};

const UpdateQuotaInvoicingDetailsForm = ({
	onCancel,
	onSuccess,
	quota,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const clientContactPersonSelectOptions = useClientsContactPersonsSelectOptions(quota.clientId);
	const clientsInvoiceRecipientSelectOptions = useClientsInvoiceRecipientSelectOptions(quota.clientId);

	const schema = useMemo(() => {
		return yup.object({
			clientContactPersonId: yup.string().nullable().default(null),
			budgetCents: yup.number().required().min(1, "Das Kontingent muss größer als 0 € sein."),
			invoiceDeliveryStartDate: yup.date().required(),
			invoiceDeliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("invoiceDeliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
			invoiceRecipientId: yup.string().nullable().default(null),
			orderNumber: yup.string().required("Bitte gib eine Auftragsnummer ein."),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup.number()
				.nullable()
				.when("paymentTargetDays", (field1Value: number[], schema) => {
					return field1Value[0] === 0
						? schema.required()
						: schema.nullable();
				}),
		}).test("atLeastOneField", "Bitte wähle einen Kontakt oder zentralen Rechnungsempfänger aus.", (value) =>
			Boolean(value.clientContactPersonId || value.invoiceRecipientId),
		);
	}, []);

	const defaultValues = useMemo(() => {
		const { paymentTargetDays, paymentTargetDaysCustom } = getPaymentTargetDefaultValues(
			quota.invoice.paymentTargetDays,
		);

		return {
			clientContactPersonId: quota.clientContactPersonId,
			budgetCents: quota.budgetCentsPlanned,
			invoiceDeliveryStartDate: quota.invoiceDeliveryStartDate ? new Date(quota.invoiceDeliveryStartDate) : undefined,
			invoiceDeliveryEndDate: quota.invoiceDeliveryEndDate ? new Date(quota.invoiceDeliveryEndDate) : undefined,
			invoiceRecipientId: quota.invoiceRecipientId,
			orderNumber: quota.orderNumber,
			paymentTargetDays: paymentTargetDays,
			paymentTargetDaysCustom: paymentTargetDaysCustom,
		};
	}, [quota.budgetCentsPlanned, quota.clientContactPersonId, quota.invoice.paymentTargetDays, quota.invoiceDeliveryEndDate, quota.invoiceDeliveryStartDate, quota.invoiceRecipientId, quota.orderNumber]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid, errors },
		setValue,
		trigger,
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const updateData: UpdateQuotaInvoicingDetailsData = {
				...data,
				paymentTargetDays: data.paymentTargetDays === 0 ? data.paymentTargetDaysCustom || 0 : data.paymentTargetDays,
				invoiceDeliveryEndDate: formatDateToYYYYMMDD(data.invoiceDeliveryEndDate),
				invoiceDeliveryStartDate: formatDateToYYYYMMDD(data.invoiceDeliveryStartDate),
			};
			await updateQuotaInvoicingDetails(quota.clientId, quota.id, updateData);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [onSuccess, quota.clientId, quota.id]);


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungsdetails bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Budget">
						<CurrencyInput name="budgetCents"
									   useControllerError={true}
									   control={control} />
					</FormSection>
					<FormSection title="Rechnungsdetails">
						<div className="space-y-4">
							<Input name="orderNumber"
								   control={control}
								   label="Bestellnummer" />
							<div className="grid grid-cols-2 gap-x-4">
								<DatePickerInput name="invoiceDeliveryStartDate"
												 control={control}
												 clearable={false}
												 label="Leistungszeitraum Start" />
								<DatePickerInput name="invoiceDeliveryEndDate"
												 control={control}
												 clearable={false}
												 label="Leistungszeitraum Ende" />
							</div>
							<InvoiceRecipientFormSection
								control={control}
								clientContactPersonSelectOptions={clientContactPersonSelectOptions}
								isSubmitted={isSubmitted}
								// @ts-expect-error - needs to be fixed
								errorMessage={errors[""]?.message}
								invoiceRecipientSelectOptions={clientsInvoiceRecipientSelectOptions}
								setValue={setValue}
								trigger={trigger}
								watch={watch}
							/>
							<PaymentTargetDaysInput
								control={control}
								showCustomPaymentTargetInput={showCustomPaymentTargetInput}
							/>
						</div>

					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.update")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateQuotaInvoicingDetailsForm;
